import React, { useState } from 'react'
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom'
import { useAuth } from '../context/useAuth'
import { APP_VERSION } from '../version'

export default function Login() {
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const emailChanged = searchParams.get('email_changed') === '1'
  const registerMessage = location.state?.message
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError('')
    setLoading(true)
    try {
      await login(email, password, rememberMe)
      navigate('/dashboard')
    } catch (err) {
      const msg = err?.message || ''
      if (msg.includes('gesperrt') || msg.includes('Konto gesperrt')) {
        setError('Ihr Konto wurde gesperrt. Bitte wenden Sie sich an den Administrator.')
      } else if (msg.includes('bestätigen') || msg.includes('Aktivierungslink') || msg.includes('E-Mail-Adresse')) {
        setError('email_not_verified')
      } else if (msg.includes('Invalid email') || msg.includes('password')) {
        setError('E-Mail oder Passwort falsch.')
      } else {
        setError(msg || 'Anmeldung fehlgeschlagen.')
      }
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-studio-bg px-4 py-6 safe-area-padding">
      <div className="card-glass w-full max-w-md p-6 sm:p-8">
        <div className="flex justify-center" aria-hidden>
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-[#00d4aa] to-[#00a8c8] text-2xl font-bold shadow-glow">
            ◇
          </div>
        </div>
        <h1 className="mt-6 text-center text-2xl font-bold text-white">
          <span className="bg-gradient-to-r text-[#00d4aa]">
            AI Mastering Studio
          </span>
        </h1>
        <p className="mt-2 text-center text-white/60">Anmelden</p>
        <form onSubmit={handleSubmit} className="mt-8 space-y-5">
          {registerMessage && (
            <div className="rounded-xl border border-[#00d4aa]/30 bg-[#00d4aa]/10 px-4 py-3 text-sm text-[#99f6e4]">
              {registerMessage}
            </div>
          )}
          {emailChanged && (
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
              E-Mail wurde geändert. Bitte mit der neuen E-Mail anmelden.
            </div>
          )}
          {error && (
            <div role="alert" className="rounded-xl border border-red-500/40 bg-red-500/15 px-4 py-3 text-sm font-medium text-red-200">
              {error === 'email_not_verified' ? (
                <div className="space-y-3">
                  <p className="mb-0">Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse. Prüfen Sie Ihren Posteingang und klicken Sie den Aktivierungslink.</p>
                  <p className="mb-0">
                    <Link
                      to={email ? `/resend-verify-email?email=${encodeURIComponent(email)}` : '/resend-verify-email'}
                      className="inline-block rounded-xl bg-[#00d4aa] hover:bg-[#1ae0b8] text-[#0a0a0f] px-4 py-2.5 text-sm font-semibold text-white no-underline border border-[#00d4aa]/50"
                    >
                      Aktivierungslink erneut senden
                    </Link>
                  </p>
                </div>
              ) : (
                error
              )}
            </div>
          )}
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-white/80">E-Mail</span>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:border-[#00d4aa] focus:ring-2 focus:ring-[#00d4aa]/20"
              placeholder="name@beispiel.de"
            />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-white/80">Passwort</span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-white placeholder-white/40 focus:border-[#00d4aa] focus:ring-2 focus:ring-[#00d4aa]/20"
            />
            <p className="mt-2 text-right space-x-3">
              <Link to="/forgot-password" className="text-sm font-medium text-[#00d4aa] hover:text-[#99f6e4] hover:underline">
                Passwort vergessen?
              </Link>
              <Link to="/resend-verify-email" className="text-sm font-medium text-[#00d4aa] hover:text-[#99f6e4] hover:underline">
                Aktivierungslink erneut senden
              </Link>
            </p>
          </label>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={rememberMe}
              onChange={(e) => setRememberMe(e.target.checked)}
              className="rounded border-white/20 bg-white/5 text-[#00d4aa] focus:ring-[#00d4aa]/20"
            />
            <span className="text-sm text-white/80">Angemeldet bleiben (30 Tage)</span>
          </label>
          <button
            type="submit"
            className="btn-gradient w-full min-h-[48px] py-3 text-base"
            disabled={loading}
          >
            {loading ? 'Bitte warten…' : 'Anmelden'}
          </button>
        </form>
        <p className="mt-6 text-center text-sm text-white/70">
          Noch kein Konto? <Link to="/register" className="text-[#00d4aa] hover:underline">Registrieren</Link>
        </p>
        <p className="mt-6 text-center text-xs text-white/40" aria-hidden>
          Build {APP_VERSION}
        </p>
      </div>
    </div>
  )
}
